import { useFeatureGate } from '@/features/billing/gates';
import type { ColorPalette } from '@/features/colors/color.types';
import {
  COLOR_PALETTES,
  PALETTE_COLOR_NAMES,
} from '@/features/colors/palettes';
import { Box, Flex, Icon, Text } from '@/ui';
import { Option, SelectDropdown } from '../SelectDropdown';

// ── Palette dot strip ──────────────────────────────────────────────────────────

type PaletteDotsProps = { palette: ColorPalette };

const PaletteDots = ({ palette }: PaletteDotsProps) => (
  <Flex gap={1}>
    {PALETTE_COLOR_NAMES.slice(0, 6).map((colorName) => {
      const {
        shade: { 500: color },
      } = palette.colors[colorName];
      return (
        <Box
          bg={color}
          borderRadius="full"
          boxShadow="inset 0 0 0 1px rgba(0,0,0,0.1)"
          flexShrink={0}
          h="11px"
          key={color}
          w="11px"
        />
      );
    })}
  </Flex>
);

// ── PaletteSelect ──────────────────────────────────────────────────────────────

type Props = {
  value: string;
  onChange: (paletteId: string) => void;
  onPreview: (paletteId: string | null) => void;
};

const CustomPaletteFooter = () => {
  const { isGated, promptUpgrade } = useFeatureGate('customPalette');

  // Building a custom palette isn't implemented yet, so the only thing this row can
  // do is sell the upgrade. That makes `isGated` the right test rather than
  // `isLocked`: an entitled user has nothing to click here, and while entitlement is
  // still resolving, offering the upgrade could upsell someone who already paid. It
  // comes back for everyone when the feature lands.
  if (!isGated) {
    return null;
  }

  return (
    <Box
      borderTopColor="line.soft"
      borderTopStyle="solid"
      borderTopWidth="1px"
      mt={1}
      pb={1}
      pt={1}
    >
      <Flex
        _hover={{ bg: 'magicPurple.25' }}
        align="center"
        as="button"
        borderRadius="ah-md"
        cursor="pointer"
        gap={2}
        mx={1.5}
        px={2}
        py={2}
        type="button"
        w="calc(100% - 12px)"
        onClick={promptUpgrade}
      >
        <Icon color="magicPurple.500" icon="Crown" size={4} />
        <Text color="magicPurple.600" fontSize="ah-control" fontWeight={600}>
          Create custom palette
        </Text>
      </Flex>
    </Box>
  );
};

export const PaletteSelect = ({ value, onChange, onPreview }: Props) => {
  // Resolved once for the dropdown rather than per option: every option would
  // otherwise repeat the same gate and profile read to reach the same answer.
  const { isGated, isLocked, promptUpgrade } = useFeatureGate('premiumPalette');

  return (
    <SelectDropdown
      footer={<CustomPaletteFooter />}
      menuProps={{ right: 0 }}
      options={COLOR_PALETTES}
      renderOption={(palette, isActive) => {
        const requiresPaidPlan = Boolean(palette.requiresPaidPlan);

        return (
          <Option
            isActive={isActive}
            // A paid palette stays in the list and offers the upgrade rather than
            // vanishing — the same treatment as a paid vibe. Locked on `isLocked` so
            // it can't be selected before entitlement resolves, but the upgrade is
            // only offered once `isGated` confirms one would help; Option treats a
            // missing handler as a no-op, so mid-load the option is simply inert.
            isLocked={requiresPaidPlan && isLocked}
            key={palette.id}
            option={palette}
            onLockedClick={
              requiresPaidPlan && isGated ? promptUpgrade : undefined
            }
          >
            <Box flex={1} minW={0}>
              <Text
                color={isActive ? 'magicPurple.700' : 'ink.strong'}
                fontSize="ah-control"
                fontWeight={700}
              >
                {palette.name}
              </Text>
              <Text color="ink.muted" fontSize="ah-status" mt={0.5}>
                {palette.description}
              </Text>
            </Box>
            <PaletteDots palette={palette} />
          </Option>
        );
      }}
      renderTrigger={(active) => (
        <Flex align="center" gap={2}>
          <Text color="ink.strong" fontSize="ah-control" fontWeight={700}>
            {active.name}
          </Text>
          <PaletteDots palette={active} />
        </Flex>
      )}
      value={
        COLOR_PALETTES.some((palette) => palette.id === value)
          ? value
          : COLOR_PALETTES[0].id
      }
      onChange={onChange}
      onPreview={onPreview}
    />
  );
};
